// Deteccao proativa de deploy novo + reload silencioso na proxima navegacao.
// Numa aba que ficou aberta, quando sobe um build novo na DigitalOcean os hashes
// dos chunks mudam e os antigos somem do servidor; o app continua rodando o
// codigo velho. O lazyComReload so resolve quem navega pra um chunk lazy ainda
// nao baixado (toma ChunkLoadError); quem circula por telas ja carregadas fica
// preso ate dar F5. Aqui detectamos a versao nova de forma proativa e, na
// PROXIMA navegacao (ponto seguro: o usuario ja esta saindo da tela), damos um
// window.location.reload() -- sem banner, sem aviso.
//
// Como funciona:
// - baseline: a "assinatura" da versao rodando AGORA. E o array 'entrypoints'
//   do asset-manifest da PRIMEIRA carga, lido por fetch no boot e guardado em
//   memoria (nao no DOM -- ver nota sobre runtime-main abaixo).
// - deteccao: a cada ~20min e quando a aba volta a ficar visivel, faz fetch do
//   asset-manifest.json (com cache-buster) e compara o 'entrypoints' de la com a
//   assinatura-baseline. Diferente => marca pendente. visibilitychange so
//   DETECTA, nunca recarrega.
// - acao: no evento hashchange (toda navegacao do HashRouter muda o hash), se
//   pendente, navega pra MESMA rota com um cache-buster (?v=) que forca o edge
//   da CDN do App Platform a buscar o index.html novo na origin (reload() puro
//   pegaria o index.html VELHO do edge). NAO recarrega fora da navegacao.
//
// POR QUE 'entrypoints' e nao files['main.js']: no CRA 4 o runtime do webpack
// (que carrega o mapa id->hash de TODOS os chunks lazy/vendor) fica INLINADO no
// index.html, e o main.<hash>.chunk.js referencia os lazy SO por id numerico --
// zero hash de chunk dentro dele. Logo o hash do main.js e INVARIANTE a deploys
// que mexem so num chunk lazy, no vendor, no css ou em modulos compartilhados
// fora do shell eager (a MAIORIA dos deploys, e justamente o caso "circula por
// telas ja carregadas"). Comparar so o main.js seria um falso-negativo
// estrutural. O array 'entrypoints' inclui o runtime-main.<hash>.js, cujo hash
// muda sempre que QUALQUER chunk muda (o mapa de hashes vive nele) -- e o sinal
// COMPLETO de "subiu build novo".
//
// POR QUE baseline por fetch e nao lido do DOM: o runtime-main NAO aparece como
// <script src> no index.html (esta inlinado); os unicos <script src> sao o
// vendor 45.*.chunk.js e o main.*.chunk.js. Entao o DOM nao expoe a assinatura
// completa. Lendo a baseline do MESMO manifest que sera comparado depois,
// baseline e comparacao vem da mesma fonte -- sem janela de divergencia
// DOM-vs-manifest (que um index.html cacheado poderia abrir) e sem depender de
// nenhum filename casar regex.
//
// Loop: apos o reload o index.html novo entrega os assets com hash novo, o boot
// faz o fetch e guarda a NOVA assinatura como baseline, o manifest passa a bater
// e nunca mais dispara -- sem precisar de guard. O guard em sessionStorage
// abaixo so existe pro caso patologico de um index.html cacheado reentregar a
// versao velha apos o reload (ai sim viraria loop atado a navegacao); chaveado
// pela ASSINATURA-ALVO (a versao remota pra qual ja recarregamos), ele suprime
// esse loop de forma estavel mesmo que a baseline-velha oscile. No caminho feliz
// nem chega a ser consultado.

// de quanto em quanto tempo re-checa o manifest em background. 20min porque
// deploy nao e frequente e o visibilitychange ja cobre a aba que ficou parada;
// intervalo mais curto so geraria trafego (o fetch e no-store, sem 304),
// relevante na WebView do app pro em dados moveis.
const INTERVALO_CHECK_MS = 20 * 60 * 1000;
// prefixo da chave de guard anti-loop (sufixada com a assinatura-alvo)
const PREFIXO_GUARD = "treino_versao_reload_";

// idempotencia: 2a chamada de iniciarAtualizacaoVersao() nao duplica
// interval/listeners; devolve um cleanup no-op.
let iniciado = false;

// tira o basename de um caminho/url: pega o ultimo segmento (sem querystring).
function basename(caminho) {
	if (!caminho) return "";
	return caminho.split("?")[0].split("/").pop();
}

// hash curto e estavel de uma string -> base36. so precisa ser unico por versao
// (cache-buster da URL no reload), nao e criptografico.
function hashCurto(str) {
	let h = 0;
	for (let i = 0; i < str.length; i++) {
		h = (Math.imul(31, h) + str.charCodeAt(i)) | 0;
	}
	return (h >>> 0).toString(36);
}

// remove o ?v= da URL depois que a pagina ja carregou fresca (ele so existia pra
// furar o edge da CDN no reload). replaceState nao recarrega nada, e cosmetico.
function limparParamVersao() {
	try {
		const url = new URL(window.location.href);
		if (url.searchParams.has("v")) {
			url.searchParams.delete("v");
			window.history.replaceState(
				null,
				"",
				url.pathname + (url.search || "") + url.hash
			);
		}
	} catch (e) {
		// se URL/history falhar, segue com o ?v= na barra (inofensivo)
	}
}

// monta a url do asset-manifest na MESMA origem do SPA, com cache-buster.
// PUBLIC_URL e '/' em prod e '' em dev; normaliza tirando a barra final pra
// nunca gerar '//asset-manifest.json' (que o browser trataria como
// protocol-relative e quebraria).
function urlManifest() {
	const base = (process.env.PUBLIC_URL || "").replace(/\/+$/, "");
	return base + "/asset-manifest.json?t=" + Date.now();
}

// extrai a assinatura da versao a partir do json do manifest: o array
// 'entrypoints' (runtime-main, vendor, css, main) reduzido aos basenames e
// unido. Se o manifest nao tiver entrypoints utilizavel, retorna null (trata
// como "nao deu pra ler" -> no-op, nunca marca pendente sem assinatura boa).
function assinaturaDoManifest(json) {
	const eps = json && json.entrypoints;
	if (!Array.isArray(eps) || eps.length === 0) return null;
	return eps.map(basename).join("|");
}

// busca o manifest e devolve a assinatura, ou null em qualquer falha
// (rede/404/json invalido/sem entrypoints). null = no-op no chamador.
async function buscarAssinatura() {
	try {
		const resp = await fetch(urlManifest(), { cache: "no-store" });
		if (!resp.ok) return null;
		const json = await resp.json();
		return assinaturaDoManifest(json);
	} catch (e) {
		// rede fora / json invalido / etc.
		return null;
	}
}

// iniciarAtualizacaoVersao() segue o idioma de PresenceService.start():
// idempotente e retorna uma funcao de cleanup pra usar no return do useEffect.
export function iniciarAtualizacaoVersao() {
	// 2a chamada: nao duplica nada.
	if (iniciado) return () => {};
	iniciado = true;

	let intervalId = null;
	// assinatura da versao rodando AGORA. Comeca null e e preenchida no 1o fetch
	// bem-sucedido; enquanto null, NAO da pra comparar (nunca marca pendente).
	let baseline = null;
	// versao remota nova ja detectada; aguardando a proxima navegacao pra recarregar.
	let pendente = false;

	// ja recarregamos por causa desta assinatura-alvo nesta aba? (guard anti-loop
	// pro caso de index.html cacheado reentregar a versao velha apos o reload).
	// chaveado pela assinatura-ALVO (a remota), que sobrevive ao reload e e
	// estavel mesmo se a baseline-velha oscilar.
	const jaRecarregouPara = (alvo) => {
		try {
			return sessionStorage.getItem(PREFIXO_GUARD + alvo) === "1";
		} catch (e) {
			return false; // se sessionStorage falhar, nao bloqueia a deteccao
		}
	};

	const marcarReloadPara = (alvo) => {
		try {
			sessionStorage.setItem(PREFIXO_GUARD + alvo, "1");
		} catch (e) {
			// best-effort; sem o guard ainda ha a protecao estrutural da baseline
		}
	};

	// busca o manifest e, se a assinatura de la for diferente da baseline, marca
	// pendente. qualquer falha e no-op: tenta no proximo ciclo. nunca marca
	// pendente sem baseline confiavel.
	const verificar = async () => {
		if (pendente) return; // ja sabemos que tem versao nova; nem refaz o fetch
		const remota = await buscarAssinatura();
		if (!remota) return; // leitura ruim: re-checa no proximo ciclo
		// 1o fetch bem-sucedido define a baseline; so compara a partir do 2o.
		if (!baseline) {
			baseline = remota;
			return;
		}
		if (remota === baseline) return;
		// versao nova detectada. guard anti-loop: se ja recarregamos pra ESTA
		// assinatura-alvo e mesmo assim a baseline continua a velha (index.html
		// cacheado reentregou o bundle antigo), nao re-arma -- senao seria loop
		// atado a navegacao.
		if (jaRecarregouPara(remota)) return;
		pendente = remota;
	};

	// reload so na navegacao: toda navegacao do HashRouter muda o hash.
	const onHashChange = () => {
		if (!pendente) return;
		// marca o alvo ANTES de navegar pra suprimir loop no caso patologico.
		marcarReloadPara(pendente);
		// reload() puro pegaria o index.html VELHO do edge da CDN do App Platform
		// (s-maxage=86400 / 24h, e static site nao deixa baixar esse TTL). Uma query
		// unica forca CDN MISS -> origin -> index.html novo (confirmado: ?t= no
		// manifest volta cf-cache-status: MISS). O HashRouter ignora a query e
		// roteia pelo hash, entao a rota atual e preservada. replace() nao deixa
		// entrada de historico pra versao velha.
		try {
			const url = new URL(window.location.href);
			url.searchParams.set("v", hashCurto(pendente));
			window.location.replace(url.toString());
		} catch (e) {
			// fallback: se URL nao existir, ao menos recarrega.
			window.location.reload();
		}
	};

	// quando a aba volta a ficar visivel, re-checa (cobre a aba que ficou horas
	// em segundo plano). visibilitychange so DETECTA, nunca recarrega.
	const onVisibilityChange = () => {
		if (document.visibilityState === "visible") {
			verificar();
		}
	};

	window.addEventListener("hashchange", onHashChange);
	document.addEventListener("visibilitychange", onVisibilityChange);
	intervalId = setInterval(verificar, INTERVALO_CHECK_MS);

	// limpa um eventual ?v= deixado pelo reload de uma versao anterior.
	limparParamVersao();
	// boot: primeira checagem (best-effort) arma a baseline cedo.
	verificar();

	return () => {
		window.removeEventListener("hashchange", onHashChange);
		document.removeEventListener("visibilitychange", onVisibilityChange);
		if (intervalId) {
			clearInterval(intervalId);
			intervalId = null;
		}
		// permite re-iniciar limpo se App remontar (ex: em testes).
		iniciado = false;
	};
}
